
import React, { useContext, useState } from 'react';
import Modal from './Modal';
import Button from '../ui/Button';
import FileUpload from '../ui/FileUpload';
import RichTextEditor from '../ui/RichTextEditor';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
import { message } from 'antd';
import { NecttosContext } from '../../../context/NecttosContext';

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AnnouncementData) => void;
  subjectId: string,
  modules: any[]
}

interface AnnouncementData {
  title: string;
  message: string;
  attachments: File[];
  priority: 'low' | 'normal' | 'high';
  moduleId: string,
  subjectId: string
  createdBy: string
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ isOpen, onClose, onSubmit, subjectId, modules }) => {
  const [formData, setFormData] = useState<AnnouncementData>({
    title: '',
    message: '',
    attachments: [],
    priority: 'normal',
    moduleId: "",
    subjectId: subjectId,
    createdBy: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { collegeId } = useContext(NecttosContext);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Announcement title is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
       const instance = await getAxiosTokenInstance()
        
        if (!validateForm()) {
          return;
        }
    
        setIsSubmitting(true);
        
        try {
          const resp = await instance.post(`/college/lms/announcements/createAnnouncement`, {...formData, collegeId:collegeId});
          if(resp?.data){
            console.log(resp?.data.message)
           message.success('Announcement Added')
          }
        }
        catch (error) {
      console.error('Error creating announcement:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      message: '',
      attachments: [],
      priority: 'normal',
      moduleId: "",
      subjectId: subjectId,
      createdBy: ''
    });
    setErrors({});
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-gray-600', bgColor: 'bg-gray-100' },
    { value: 'normal', label: 'Normal', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { value: 'high', label: 'High', color: 'text-red-600', bgColor: 'bg-red-100' }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Announcement" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title and Priority */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="announcementTitle" className="block text-sm font-medium text-gray-700 mb-2">
              📢 Announcement Title *
            </label>
            <input
              type="text"
              id="announcementTitle"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter announcement title"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as 'low' | 'normal' | 'high' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            {/* Priority Indicator */}
            <div className="mt-2">
              {priorityOptions.map((option) => (
                formData.priority === option.value && (
                  <span
                    key={option.value}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${option.bgColor} ${option.color}`}
                  >
                    {option.label} Priority
                  </span>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Message Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message Content *
          </label>
          <RichTextEditor
            value={formData.message}
            onChange={(value) => setFormData(prev => ({ ...prev, message: value }))}
            placeholder="Enter your announcement message. Use formatting to highlight important information..."
            minHeight="250px"
          />
          {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
        </div>

           <label htmlFor="module" className="block text-sm font-medium text-gray-700 mb-2">
    Module *
  </label>

  <select
    id="module"
    value={formData.moduleId}
    onChange={(e) => setFormData(prev => ({ ...prev, moduleId: e.target.value }))}
    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      errors.module ? 'border-red-500' : 'border-gray-300'
    }`}
  >
    <option value="">Select a module</option>
    {modules?.map((mod) => (
      <option key={mod._id} value={mod._id}>
        {mod.fullModuleName}
      </option>
    ))}
  </select>
           <div className="md:col-span-2">
            <label htmlFor="announcementTitle" className="block text-sm font-medium text-gray-700 mb-2">
              📢 Announcement Created By *
            </label>
            <input
              type="text"
              id="createdBy"
              value={formData.createdBy}
              onChange={(e) => setFormData(prev => ({ ...prev, createdBy: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter Publisher Name"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

        {/* Preview Section */}
        {(formData.title || formData.message) && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Preview:</h4>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              {formData.title && (
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{formData.title}</h3>
                  {priorityOptions.map((option) => (
                    formData.priority === option.value && (
                      <span
                        key={option.value}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${option.bgColor} ${option.color}`}
                      >
                        {option.label}
                      </span>
                    )
                  ))}
                </div>
              )}
             
              {formData.message && (
                <div 
                  className="text-gray-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.message }}
                />
              )}
             
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Publishing...
              </span>
            ) : (
              'Publish Announcement'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AnnouncementModal;
